import type { HelpArticle } from "@/lib/registrant-help/types";

export type PartyRole = "OMT" | "AMS" | "PM";

export const PARTY_ROLE_BLURBS: Record<
  PartyRole,
  { title: string; body: string }
> = {
  OMT: {
    title: "OMT — Ordinance / Mortgage Trustee",
    body: "The trustee contact listed on this registration.",
  },
  AMS: {
    title: "AMS — Asset Manager / Mortgage Servicer",
    body: "The asset manager or mortgage servicer listed on this registration.",
  },
  PM: {
    title: "PM — Property Manager",
    body: "The property manager listed on this registration.",
  },
};

/** Role tag on a party question, preferring a leading [TAG] over a later (TAG). */
export function partyRoleFromQuestionText(text: string): PartyRole | null {
  const square = text.match(/\[\s*(OMT|AMS|PM)\s*\]/i);
  if (square) return square[1].toUpperCase() as PartyRole;
  const parens = [...text.matchAll(/\(\s*(OMT|AMS|PM)\s*\)/gi)];
  const last = parens.at(-1);
  if (last) return last[1].toUpperCase() as PartyRole;
  const normalized = text
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  if (/^(OMT|AMS|PM)$/i.test(normalized)) {
    return normalized.toUpperCase() as PartyRole;
  }
  if (/property\s*manager/i.test(normalized)) return "PM";
  if (/asset\s*manager|mortgage\s*servicer/i.test(normalized)) return "AMS";
  if (
    /mortgage\s*trustee|mortgagee|ordinance\s*\/\s*mortgage/i.test(normalized)
  ) {
    return "OMT";
  }
  const word = normalized.match(/\b(OMT|AMS|PM)\b/i);
  if (word) return word[1].toUpperCase() as PartyRole;
  return null;
}

/** Shared definitions for the three party roles on a registration. */
export const PARTIES_HELP_ARTICLE: HelpArticle = {
  id: "parties",
  title: "OMT, AMS, and PM",
  summary:
    "What OMT, AMS, and PM mean on a registration, and who can use the OMT/AMS portal.",
  category: "registrations",
  keywords: [
    "omt",
    "ams",
    "pm",
    "property manager",
    "asset manager",
    "mortgage servicer",
    "mortgage trustee",
    "ordinance",
    "owner",
    "party",
    "exempt",
  ],
  related: [
    "party-exempt",
    "registrations",
    "registration-details",
    "service-transfer",
  ],
  sections: [
    {
      heading: "OMT — Ordinance / Mortgage Trustee",
      blocks: [
        {
          type: "p",
          text: "OMT is the ordinance or mortgage trustee listed on a registration. The question is often labeled Owner or Ordinance / Mortgage Trustee and tagged [OMT].",
        },
        {
          type: "p",
          text: "Changing the OMT can start a service transfer. An organization listed as OMT can sign in to the OMT/AMS portal and review those registrations. If that party is marked Exempt, see Exempt for what that does to fees due and Needs Attention.",
        },
      ],
    },
    {
      heading: "AMS — Asset Manager / Mortgage Servicer",
      blocks: [
        {
          type: "p",
          text: "AMS is the asset manager or mortgage servicer listed on the registration, tagged [AMS]. An organization listed as AMS uses the same OMT/AMS portal to view those registrations. The portal is view-only: payments and registration changes stay in the registrant portal.",
        },
      ],
    },
    {
      heading: "PM — Property Manager",
      blocks: [
        {
          type: "p",
          text: "PM is the property manager for the property, tagged [PM] or labeled Property Manager. This is the day-to-day management contact stored on the registration. The PM role by itself does not open the OMT/AMS portal.",
        },
      ],
    },
    {
      heading: "Not the same as Ordinance Management Type",
      blocks: [
        {
          type: "p",
          text: "Ordinance Management Type is a separate jurisdiction setting (Update or Change) that controls how an OMT party change is processed. It is not the OMT contact on the registration.",
        },
      ],
    },
  ],
};

/** What the Exempt switch on a party does. Opened from that switch. */
export const PARTY_EXEMPT_HELP_ARTICLE: HelpArticle = {
  id: "party-exempt",
  title: "Exempt",
  summary:
    "The Exempt switch on a party. It applies only when that party is the OMT on a registration.",
  category: "registrations",
  keywords: [
    "exempt",
    "omt",
    "party",
    "fees due",
    "late fee",
    "registration fee",
    "needs attention",
    "renewal",
    "overnight",
  ],
  related: ["parties", "needs-attention", "renewals"],
  sections: [
    {
      heading: "Where the switch is",
      blocks: [
        {
          type: "p",
          text: "Exempt is a switch on a party record. Hera operators turn it on or off. Government staff can see it and cannot change it.",
        },
      ],
    },
    {
      heading: "It only looks at the OMT",
      blocks: [
        {
          type: "p",
          text: "The switch applies only when this party is the answer on the registration’s OMT question, the question tagged [OMT]. The same party listed as AMS, PM, or any other contact does not get this treatment. Needs Attention uses that same OMT answer.",
        },
      ],
    },
    {
      heading: "Fees due are zero",
      blocks: [
        {
          type: "ul",
          items: [
            "Registration fees due and late fees due are zero.",
            "Money already collected stays on the registration. This switch does not refund it.",
            "Inspection fees are not waived by this switch.",
            "Saving Exempt updates fees due for registrations whose answers store this party’s id. The overnight fees-due refresh then applies the switch to every registration, including any the save did not reach. Turning Exempt off restores the ordinance registration and late fees on that same schedule.",
          ],
        },
      ],
    },
    {
      heading: "Registration and renewal are still required",
      blocks: [
        {
          type: "p",
          text: "A zero balance from Exempt does not by itself mark the period paid or finish registration or renewal. The registrant still has to complete the form. On the Registration Periods & Payment or Renewal Periods & Payment step, Hera explains that the selected OMT is Exempt and shows Submit Registration or Submit Renewal instead of requiring a payment when no inspection fee is due. After that submission, the registration follows the normal review or approval rules.",
        },
      ],
    },
    {
      heading: "Needs Attention",
      blocks: [
        {
          type: "p",
          text: "A late renewal stays off the payment side of Needs Attention, including the Monday and Wednesday email, when the OMT is exempt. An open compliance issue can still place that registration on Needs Attention.",
        },
      ],
    },
  ],
};
